import { useMemo, useRef } from "react"
import { useSelector } from "react-redux"
import { DateTime } from "luxon"
import Button from "react-bootstrap/Button"
import Table from "react-bootstrap/Table"
import { useReactToPrint } from "react-to-print"
import { FiPrinter } from "react-icons/fi"
import "./index.scss"

import { limsRelease } from "state_management"
import { isQaApproved, effectiveOverall } from "state_management/lims/limsResults"
import useT from "utils/hooks/useT"
import { displaySampleId } from "utils/scripts/sampleId"

// Certificate of Analysis (F-C5) — a printable BATCH certificate for a RELEASED sample (lot +
// observation id). It is a DERIVED document: every figure comes from the QC results that were
// QA-approved (SPEC-012/013) and the QA release (F-C4). Nothing new is persisted. (SPEC-021 B4:
// the audit trail is server-derived from mutations; a read-only certificate view writes none —
// the release itself is the audited, signed act the certificate derives from.)
//
// SPEC-013: the certified figures are the QA-APPROVED results — each analysis row certifies the
// `qa-approved` result for its sample. (SPEC-021 cleanup: the tolerant fallback to a legacy
// reviewer-signed result is gone — no pre-A3/mock-seeded releases exist after the cutover.)
//
// Props: group — the Sample View sample group ({ productName, sampleName, observationId,
// customerName, analyses: [{ analysisId, sampleId, name }] }).
export default function CertificateOfAnalysis({ group }) {
  const { t } = useT("common")
  const printRef = useRef(null)

  const results = useSelector((state) => state.limsResults.items)
  const release = useSelector((state) => limsRelease.selectRelease(state, group))
  const members = useSelector((state) => state.members)

  const analyses = useMemo(() => (group && group.analyses) || [], [group])

  // The certified result for each analysis on this sample, matched by sampleId (unique per
  // analysis-sample). Carries the snapshotted spec + judged values (GMP: the figures certified
  // are exactly those that were QA-approved). SPEC-013: only a `qa-approved` result certifies.
  const rows = useMemo(
    () =>
      analyses.map((a) => {
        const result = results.find((r) => r.sampleId === a.sampleId && isQaApproved(r)) || null
        return { analysis: a, result }
      }),
    [analyses, results]
  )

  const handlePrint = useReactToPrint({ contentRef: printRef })

  const initials = (memberId) => (memberId && members[memberId] && members[memberId].initials) || "—"
  const fmtDate = (iso) => (iso ? DateTime.fromISO(iso).toFormat("dd/MM/yyyy HH:mm") : "—")

  // Overall: PASS when every analysis has a reviewer-signed result whose EFFECTIVE verdict is not
  // FAIL. SPEC-011: a reviewer okOverride turns a computed FAIL into an effective PASS, so the CoA
  // verdict uses effectiveOverall (the raw computed FAIL remains on the record for traceability).
  const allPass =
    rows.length > 0 && rows.every((r) => r.result && effectiveOverall(r.result) !== "FAIL")

  // SPEC-020 (C2) override disclosure: the certified results whose computed FAIL was accepted by
  // an e-signed reviewer okOverride. The certificate must never show an overall PASS without
  // declaring these — each failed row gets an inline marker and the disclosure section below
  // lists the signer, date and reason per override.
  const overrides = rows.filter(({ result }) => result && result.okOverride && result.overall === "FAIL")

  return (
    <div className="CoA">
      <div className="CoA-toolbar">
        <Button variant="secondary" onClick={handlePrint}>
          <FiPrinter /> {t("limsCoaPrint")}
        </Button>
      </div>

      <div className="CoA-sheet" ref={printRef}>
        <h2 className="CoA-title">{t("limsCoaTitle")}</h2>

        <div className="CoA-meta">
          <div>
            <span className="CoA-meta-label">{t("limsCoaProduct")}</span>
            <span>{group.productName || t("none")}</span>
          </div>
          <div>
            <span className="CoA-meta-label">{t("limsCoaSample")}</span>
            <span>{group.sampleName || "—"}</span>
          </div>
          <div>
            <span className="CoA-meta-label">{t("limsCoaSampleId")}</span>
            {/* #4145 — DISPLAY. This is a GMP certificate: it prints the lot's number, never
                the uniqueness suffix. `group.observationId` itself is untouched — the release
                lookup at the top of this component keys on it (`limsRelease.selectRelease`), and
                stripping it there would make the certificate stop finding its own release. */}
            <span>{displaySampleId(group.observationId) ?? "—"}</span>
          </div>
          {group.customerName && (
            <div>
              <span className="CoA-meta-label">{t("limsCoaCustomer")}</span>
              <span>{group.customerName}</span>
            </div>
          )}
          <div>
            <span className="CoA-meta-label">{t("limsCoaReleasedBy")}</span>
            <span>{initials(release && release.releasedBy)}</span>
          </div>
          <div>
            <span className="CoA-meta-label">{t("limsCoaReleasedAt")}</span>
            <span>{fmtDate(release && release.releasedAt)}</span>
          </div>
        </div>

        {rows.map(({ analysis, result }, idx) => (
          <div className="CoA-analysis" key={analysis.sampleId || idx}>
            <h3 className="CoA-analysis-name">{analysis.name}</h3>
            {!result ? (
              <p className="CoA-no-result">{t("limsCoaNoResult")}</p>
            ) : (
              <>
                <Table className="CoA-table" bordered size="sm">
                  <thead>
                    <tr>
                      <th>{t("limsCoaParameter")}</th>
                      <th>{t("limsCoaUnit")}</th>
                      <th>{t("limsCoaCriterion")}</th>
                      <th>{t("limsCoaResult")}</th>
                      <th>{t("limsCoaVerdict")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(result.values || []).length === 0 ? (
                      <tr>
                        <td colSpan={4}>—</td>
                        <td>{verdict(null, t)}</td>
                      </tr>
                    ) : (
                      result.values.map((v) => (
                        <tr key={v.parameterId}>
                          <td>{v.name}</td>
                          <td>{v.unit || "—"}</td>
                          <td>{criterionText(v)}</td>
                          <td>{v.value === null || v.value === undefined ? "—" : String(v.value)}</td>
                          <td>
                            {verdict(v.pass, t)}
                            {/* SPEC-020: a failed row under an e-signed okOverride says so right
                                where the Fail is printed — the row and the overall verdict must
                                never contradict each other silently. */}
                            {result.okOverride && result.overall === "FAIL" && v.pass === false && (
                              <span className="CoA-override-marker"> — {t("limsCoaOverrideMarker")}</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
                {/* #4108: name every signer, not just the reviewer — the person who PERFORMED the
                    analysis belongs on the document certifying it, as does the supervisor on the
                    methods that require one. The supervisor line is omitted entirely when there was
                    no supervisor step, rather than printed with an em dash: an empty supervisor slot
                    on a GMP document invites the reading that a required review is missing. Mirrors
                    the server-generated certificate (backend routes/lims/certificate.py). */}
                <p className="CoA-reviewed-by">
                  {t("limsCoaAnalysedBy")}: {initials(result.enteredBy)} · {fmtDate(result.enteredAt)}
                </p>
                <p className="CoA-reviewed-by">
                  {t("limsCoaReviewedBy")}: {initials(result.reviewedBy)} · {fmtDate(result.reviewedAt)}
                </p>
                {result.supervisedBy && (
                  <p className="CoA-reviewed-by">
                    {t("limsCoaSupervisedBy")}: {initials(result.supervisedBy)} · {fmtDate(result.supervisedAt)}
                  </p>
                )}
              </>
            )}
          </div>
        ))}

        {/* SPEC-020 (C2) disclosure section: one line per e-signed review override — signer
            initials, date and the signed reason. Printed ABOVE the overall verdict so a PASS is
            always read together with its declared deviations. */}
        {overrides.length > 0 && (
          <div className="CoA-overrides">
            <h3 className="CoA-overrides-title">{t("limsCoaOverridesHeading")}</h3>
            {overrides.map(({ analysis, result }, idx) => {
              const sig = result.okOverrideSignature
              return (
                <p className="CoA-override-line" key={analysis.sampleId || idx}>
                  <strong>{analysis.name}</strong>:{" "}
                  {t("limsCoaOverrideSignedBy", {
                    initials: initials(sig && sig.memberId),
                    date: fmtDate(sig && sig.timestamp),
                  })}
                  {sig && sig.reason ? ` — ${t("limsCoaOverrideReason")}: ${sig.reason}` : ""}
                </p>
              )
            })}
          </div>
        )}

        <div className={"CoA-overall " + (allPass ? "is-pass" : "is-fail")}>
          <span className="CoA-meta-label">{t("limsCoaOverall")}</span>
          <span>{allPass ? t("limsCoaReleased") : t("limsResultFail")}</span>
        </div>

        <p className="CoA-footer">
          {t("limsCoaElectronicNote")} · {t("limsCoaGeneratedAt")}: {fmtDate(DateTime.now().toISO())}
        </p>
      </div>
    </div>
  )
}

// Human-readable acceptance criterion from a snapshotted result value.
function criterionText(v) {
  const fmt = (x) => (x === null || x === undefined || x === "" ? "—" : String(x))
  switch (v.criterionType) {
    case "min":
      return `≥ ${fmt(v.min)}`
    case "max":
      return `≤ ${fmt(v.max)}`
    case "range":
      return `${fmt(v.min)} – ${fmt(v.max)}`
    default:
      return "—"
  }
}

function verdict(pass, t) {
  if (pass === null || pass === undefined) return "—"
  return pass ? t("limsResultPass") : t("limsResultFail")
}
